/**
 * The shared archive, on the shareholder's own page — 17 Sep 2026.
 *
 * The feedback box, the places to follow us and the discount codes moved to
 * `ShareholderHome` when the shareholder view gained a landing tab. The archive
 * stayed here, with the holding and the cap table, because it is the same kind
 * of thing: the paperwork behind owning part of a company, rather than
 * something we would like an owner to do.
 */
import { useState } from 'react'
import { fmtFileSize } from '@/shared/util/files'
import { formatDate } from '@/shared/util/dates'
import {
  downloadShareholderDoc,
  type ShareholderDoc,
} from '@/features/ownership/repository/shareholderDocsRepository'

/**
 * The shared archive.
 *
 * The list comes from Firestore, which a shareholder may read. The file does
 * not: downloading one asks `crmShareholderArchive` for the bytes, with the
 * reader's ID token on the request — which is why this is a button and not an
 * anchor with an href sitting in the markup. An anchor cannot carry a header.
 */
export function ShareholderArchiveCard({ docs }: { docs: ShareholderDoc[] }) {
  const [busy, setBusy] = useState('')
  const [error, setError] = useState<string | null>(null)

  const open = async (d: ShareholderDoc) => {
    setBusy(d.id)
    setError(null)
    try {
      await downloadShareholderDoc(d.id, d.name)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That document could not be downloaded.')
    } finally {
      setBusy('')
    }
  }

  return (
    <section className="outside__card">
      <h2 className="outside__title">Documents</h2>
      <p className="outside__lede">
        The papers that belong to all of us — the shareholder agreement, protokoller from general
        meetings, and whatever else the board files here.
      </p>

      {docs.length === 0 ? (
        <p className="empty-note">Nothing has been filed here yet.</p>
      ) : (
        <ul className="shx__docs">
          {docs.map((d) => (
            <li className="shx__doc" key={d.id}>
              <button
                className="shx__docname"
                onClick={() => void open(d)}
                disabled={busy === d.id || !d.path}
              >
                {d.name}
              </button>
              <span className="micro">
                {d.note && `${d.note} · `}
                {/* The document's own date, which is what it was sorted by. */}
                {d.date && `${formatDate(d.date)} · `}
                {fmtFileSize(d.size)}
                {busy === d.id && ' · downloading…'}
              </span>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="modal__error">{error}</p>}
    </section>
  )
}
